'use client';

/**
 * PulseDot — small filled circle with an animated pulsing ring.
 *
 * Used as the "active" indicator on wallet rows in the wallet menu, but
 * deliberately generic so future surfaces (live indicator, recording dot,
 * online status) can reuse it. Inactive wallet rows use `color="muted"`
 * with `static` for a non-pulsing tertiary dot. Honors `prefers-reduced-motion` by rendering
 * only the static dot when reduced motion is requested.
 *
 * Spec: frontend/specs/075-wallet-dropdown-redesign/supplementary/contracts/component-api.md
 */

import {
  domAnimation,
  LazyMotion,
  m,
  type TargetAndTransition,
  type Transition,
  useReducedMotion,
} from 'motion/react';
import { cn } from '@/lib/ui/utils';

export type PulseDotSize = 'xs' | 'sm' | 'md';
export type PulseDotColor = 'brand' | 'success' | 'destructive' | 'foreground' | 'muted';

export interface PulseDotProps {
  /** Visual size of the inner dot. Default: 'sm' (6px). */
  size?: PulseDotSize;
  /** Semantic color of the dot. Default: 'brand'. */
  color?: PulseDotColor;
  /** Disable the pulsing ring; only render the static dot. Default: false. */
  static?: boolean;
  className?: string;
  /** Whether the dot is decorative for assistive tech. Default: true. */
  'aria-hidden'?: boolean;
}

const SIZE_CLASS: Record<PulseDotSize, string> = {
  xs: 'size-1',
  sm: 'size-1.5',
  md: 'size-2',
};

const COLOR_CLASS: Record<PulseDotColor, string> = {
  brand: 'bg-button-primary-bg',
  success: 'bg-feedback-success-accent',
  destructive: 'bg-feedback-error-accent',
  foreground: 'bg-foreground-primary',
  muted: 'bg-background-tertiary',
};

const RING_BORDER_CLASS: Record<PulseDotColor, string> = {
  brand: 'border-button-primary-bg',
  success: 'border-feedback-success-border',
  destructive: 'border-feedback-error-border',
  foreground: 'border-border-primary',
  muted: 'border-border-primary',
};

const RING_TRANSITION: Transition = {
  duration: 1.6,
  ease: 'easeOut',
  repeat: Number.POSITIVE_INFINITY,
};

const RING_ANIMATE: TargetAndTransition = {
  scale: [1, 2.3],
  opacity: [0.4, 0],
};

export function PulseDot({
  size = 'sm',
  color = 'brand',
  static: isStatic = false,
  className,
  'aria-hidden': ariaHidden = true,
}: PulseDotProps): React.ReactNode {
  const prefersReducedMotion = useReducedMotion();
  const sizeClass = SIZE_CLASS[size];
  const colorClass = COLOR_CLASS[color];
  const ringBorderClass = RING_BORDER_CLASS[color];
  const showRing = !isStatic && prefersReducedMotion !== true;

  return (
    <span
      aria-hidden={ariaHidden}
      data-testid="pulse-dot"
      className={cn('relative inline-block shrink-0', sizeClass, className)}
    >
      <span className={cn('absolute inset-[20%] rounded-full', colorClass)} />
      {showRing ? (
        <LazyMotion features={domAnimation}>
          <m.span
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 rounded-full border will-change-transform',
              ringBorderClass,
            )}
            initial={false}
            animate={RING_ANIMATE}
            transition={RING_TRANSITION}
          />
        </LazyMotion>
      ) : null}
    </span>
  );
}
